import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Save as SaveIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Quill toolbar configuration
const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet'];

// Risk colour coding based on risk score
const getRiskColor = (risk) => {
  const r = parseInt(risk, 10);
  if (isNaN(r)) return 'inherit';
  if (r <= 4) return 'rgba(76, 175, 80, 0.18)';   // green — low
  if (r <= 9) return 'rgba(255, 235, 59, 0.18)';  // yellow — medium
  if (r <= 15) return 'rgba(255, 152, 0, 0.22)';  // orange — high
  return 'rgba(244, 67, 54, 0.22)';               // red — very high
};

const GeneratedRAMSEditor = ({
  data,
  officeWorkers = [],
  siteWorkers = [],
  contractsManagerId,
  supervisorId,
  responsiblePeopleIds = [],
  hospitalFields = {},
  onDataChange,
  onHospitalChange,
  onContractsManagerChange,
  onSupervisorChange,
  onResponsiblePeopleChange = () => {},
  onSave,
  onSavePdf,
  onDelete,
  onRevise,
  saving = false,
  savingPdf = false,
  revising = false,
  isNew = false,
  generatedRamId = null,
  defaultPdfName = '',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [reviseComments, setReviseComments] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expandedStatements, setExpandedStatements] = useState({});
  const [pdfFileName, setPdfFileName] = useState(defaultPdfName);

  useEffect(() => {
    setPdfFileName(defaultPdfName);
  }, [defaultPdfName]);

  const methodStatements = data?.method_statements || [];
  const riskAssessment = data?.risk_assessment || [];
  const plantEquipment = data?.plant_equipment || [];
  const allWorkers = [...officeWorkers, ...siteWorkers];
  const selectedResponsiblePeopleIds = Array.isArray(responsiblePeopleIds)
    ? responsiblePeopleIds
    : Array.from(responsiblePeopleIds || []);

  // ── Method statement handlers ──────────────────────────────────────

  const updateMethodStatement = useCallback((index, field, value) => {
    // Guard against no-op updates — ReactQuill fires onChange on every render
    // which would create an infinite loop without this check.
    if (methodStatements[index]?.[field] === value) return;
    const updated = [...methodStatements];
    updated[index] = { ...updated[index], [field]: value };
    onDataChange({ ...data, method_statements: updated });
  }, [methodStatements, data, onDataChange]);

  const addMethodStatement = useCallback(() => {
    onDataChange({
      ...data,
      method_statements: [...methodStatements, { title: '', content_html: '' }],
    });
  }, [methodStatements, data, onDataChange]);

  const removeMethodStatement = useCallback((index) => {
    onDataChange({
      ...data,
      method_statements: methodStatements.filter((_, i) => i !== index),
    });
  }, [methodStatements, data, onDataChange]);

  const toggleStatement = useCallback((index) => {
    setExpandedStatements(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  // ── Risk assessment handlers ───────────────────────────────────────

  const updateRiskRow = useCallback((index, field, value) => {
    const updated = [...riskAssessment];
    const row = { ...updated[index] };

    // Handle nested risk objects
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      row[parent] = { ...row[parent], [child]: value };
      // Auto-calculate risk = level × severity
      if (child === 'level' || child === 'severity') {
        const level = parseInt(child === 'level' ? value : row[parent].level, 10) || 1;
        const severity = parseInt(child === 'severity' ? value : row[parent].severity, 10) || 1;
        row[parent] = { ...row[parent], risk: level * severity };
      }
    } else {
      row[field] = value;
    }

    updated[index] = row;
    onDataChange({ ...data, risk_assessment: updated });
  }, [riskAssessment, data, onDataChange]);

  const addRiskRow = useCallback(() => {
    onDataChange({
      ...data,
      risk_assessment: [...riskAssessment, {
        task: '',
        hazard: '',
        who_exposed: '',
        existing_controls: '',
        uncontrolled_risk: { level: 1, severity: 1, risk: 1 },
        additional_controls: '',
        controlled_risk: { level: 1, severity: 1, risk: 1 },
      }],
    });
  }, [riskAssessment, data, onDataChange]);

  const removeRiskRow = useCallback((index) => {
    onDataChange({
      ...data,
      risk_assessment: riskAssessment.filter((_, i) => i !== index),
    });
  }, [riskAssessment, data, onDataChange]);

  // ── Plant & equipment handlers ─────────────────────────────────────

  const updatePlantRow = useCallback((index, field, value) => {
    const updated = [...plantEquipment];
    updated[index] = { ...updated[index], [field]: value };
    onDataChange({ ...data, plant_equipment: updated });
  }, [plantEquipment, data, onDataChange]);

  const addPlantRow = useCallback(() => {
    onDataChange({
      ...data,
      plant_equipment: [...plantEquipment, { name: '', quantity: '', notes: '' }],
    });
  }, [plantEquipment, data, onDataChange]);

  const removePlantRow = useCallback((index) => {
    onDataChange({
      ...data,
      plant_equipment: plantEquipment.filter((_, i) => i !== index),
    });
  }, [plantEquipment, data, onDataChange]);

  // ── Revision handler ───────────────────────────────────────────────

  const handleRevise = useCallback(() => {
    if (reviseComments.trim() && onRevise) {
      onRevise(reviseComments.trim());
      setReviseComments('');
    }
  }, [reviseComments, onRevise]);

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Box>
      {/* AI Revision Input */}
      {onRevise && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon fontSize="small" color="primary" />
              Revise with AI
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder="Describe changes you'd like to make..."
                value={reviseComments}
                onChange={(e) => setReviseComments(e.target.value)}
                disabled={revising}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleRevise}
                disabled={!reviseComments.trim() || revising}
                startIcon={revising ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                sx={{ minWidth: 120, mt: 0.5 }}
              >
                {revising ? 'Revising...' : 'Revise'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Header Info — Contracts Manager, Supervisor, Hospital */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Responsible Persons & Nearest Hospital
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>Contracts Manager</InputLabel>
              <Select
                value={contractsManagerId || ''}
                onChange={(e) => onContractsManagerChange(e.target.value)}
                label="Contracts Manager"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {officeWorkers.map(w => (
                  <MenuItem key={w.worker_id} value={w.worker_id}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>Supervisor</InputLabel>
              <Select
                value={supervisorId || ''}
                onChange={(e) => onSupervisorChange(e.target.value)}
                label="Supervisor"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {siteWorkers.map(w => (
                  <MenuItem key={w.worker_id} value={w.worker_id}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 260, flex: 1 }}>
              <InputLabel>Responsible People</InputLabel>
              <Select
                multiple
                value={selectedResponsiblePeopleIds}
                onChange={(e) => {
                  const value = e.target.value;
                  const ids = Array.isArray(value) ? value : String(value).split(',');
                  onResponsiblePeopleChange(ids.map(id => String(id)));
                }}
                label="Responsible People"
                renderValue={(selected) => `${selected.length} selected`}
              >
                {allWorkers.map(w => (
                  <MenuItem key={w.worker_id} value={String(w.worker_id)}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nearest Hospital
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Hospital Name" value={hospitalFields.hospital_name || ''} onChange={(e) => onHospitalChange('hospital_name', e.target.value)} sx={{ flex: 2, minWidth: 180 }} />
            <TextField size="small" label="Distance" value={hospitalFields.hospital_distance || ''} onChange={(e) => onHospitalChange('hospital_distance', e.target.value)} sx={{ flex: 1, minWidth: 100 }} />
            <TextField size="small" label="Address" value={hospitalFields.hospital_address || ''} onChange={(e) => onHospitalChange('hospital_address', e.target.value)} sx={{ flex: 2, minWidth: 180 }} />
            <TextField size="small" label="Opening Times" value={hospitalFields.hospital_opening_times || ''} onChange={(e) => onHospitalChange('hospital_opening_times', e.target.value)} sx={{ flex: 1, minWidth: 140 }} />
            <TextField size="small" label="Phone" value={hospitalFields.hospital_phone || ''} onChange={(e) => onHospitalChange('hospital_phone', e.target.value)} sx={{ flex: 1, minWidth: 120 }} />
          </Box>
        </CardContent>
      </Card>

      {/* Method Statements */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Method Statements ({methodStatements.length})
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addMethodStatement}>
              Add
            </Button>
          </Box>
          <Stack spacing={2}>
            {methodStatements.map((ms, index) => (
              <Paper key={index} variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleStatement(index)}
                >
                  <TextField
                    size="small"
                    placeholder="Method statement title"
                    value={ms.title || ''}
                    onChange={(e) => updateMethodStatement(index, 'title', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ flex: 1, mr: 1 }}
                    variant="standard"
                    InputProps={{ disableUnderline: true, sx: { fontWeight: 'bold' } }}
                  />
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeMethodStatement(index); }} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  {expandedStatements[index] !== false ? (
                    <ExpandLessIcon fontSize="small" sx={{ ml: 0.5 }} />
                  ) : (
                    <ExpandMoreIcon fontSize="small" sx={{ ml: 0.5 }} />
                  )}
                </Box>
                <Collapse in={expandedStatements[index] !== false}>
                  <Box sx={{ p: 1 }}>
                    <ReactQuill
                      theme="snow"
                      value={ms.content_html || ''}
                      onChange={(val) => updateMethodStatement(index, 'content_html', val)}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      style={{ minHeight: 200 }}
                    />
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Risk Assessment Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Risk Assessment ({riskAssessment.length} hazards)
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addRiskRow}>
              Add Row
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Hazard</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Who Is Exposed</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Existing Controls</TableCell>
                  <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider' }}>
                    Uncontrolled Risk
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Additional Controls</TableCell>
                  <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider' }}>
                    Controlled Risk
                  </TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={4}></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>L</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>S</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>R</TableCell>
                  <TableCell></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>L</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>S</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>R</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskAssessment.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.task || ''}
                        onChange={(e) => updateRiskRow(index, 'task', e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.hazard || ''}
                        onChange={(e) => updateRiskRow(index, 'hazard', e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.who_exposed || ''}
                        onChange={(e) => updateRiskRow(index, 'who_exposed', e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.existing_controls || ''}
                        onChange={(e) => updateRiskRow(index, 'existing_controls', e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </TableCell>
                    {/* Uncontrolled risk L/S/R */}
                    <TableCell align="center" sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={row.uncontrolled_risk?.level || 1}
                        onChange={(e) => updateRiskRow(index, 'uncontrolled_risk.level', e.target.value)}
                        variant="standard"
                        sx={{ width: 45 }}
                      >
                        {[1,2,3,4,5].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell align="center" sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={row.uncontrolled_risk?.severity || 1}
                        onChange={(e) => updateRiskRow(index, 'uncontrolled_risk.severity', e.target.value)}
                        variant="standard"
                        sx={{ width: 45 }}
                      >
                        {[1,2,3,4,5].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        bgcolor: getRiskColor(row.uncontrolled_risk?.risk),
                        p: 0.5,
                      }}
                    >
                      {row.uncontrolled_risk?.risk || ''}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.additional_controls || ''}
                        onChange={(e) => updateRiskRow(index, 'additional_controls', e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </TableCell>
                    {/* Controlled risk L/S/R */}
                    <TableCell align="center" sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={row.controlled_risk?.level || 1}
                        onChange={(e) => updateRiskRow(index, 'controlled_risk.level', e.target.value)}
                        variant="standard"
                        sx={{ width: 45 }}
                      >
                        {[1,2,3,4,5].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell align="center" sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={row.controlled_risk?.severity || 1}
                        onChange={(e) => updateRiskRow(index, 'controlled_risk.severity', e.target.value)}
                        variant="standard"
                        sx={{ width: 45 }}
                      >
                        {[1,2,3,4,5].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        bgcolor: getRiskColor(row.controlled_risk?.risk),
                        p: 0.5,
                      }}
                    >
                      {row.controlled_risk?.risk || ''}
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton size="small" onClick={() => removeRiskRow(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {riskAssessment.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No risk assessment rows. Click "Add Row" to start.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Plant & Equipment */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Plant & Equipment ({plantEquipment.length})
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addPlantRow}>
              Add
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plantEquipment.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.name || ''}
                        onChange={(e) => updatePlantRow(index, 'name', e.target.value)}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.quantity || ''}
                        onChange={(e) => updatePlantRow(index, 'quantity', e.target.value)}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.notes || ''}
                        onChange={(e) => updatePlantRow(index, 'notes', e.target.value)}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton size="small" onClick={() => removePlantRow(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {plantEquipment.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No plant & equipment items. Click "Add" to start.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
        {onSavePdf && !isNew && (
          <>
            <TextField
              size="small"
              label="PDF File Name"
              value={pdfFileName}
              onChange={(e) => setPdfFileName(e.target.value)}
              placeholder="RAMS - ProjectNo"
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="outlined"
              startIcon={savingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              onClick={() => onSavePdf(pdfFileName)}
              disabled={savingPdf}
              fullWidth={isMobile}
            >
              {savingPdf ? 'Saving PDF...' : 'Save to PDF'}
            </Button>
          </>
        )}
        {onDelete && !isNew && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            fullWidth={isMobile}
          >
            Delete RAMS
          </Button>
        )}
        {onSave && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={onSave}
            disabled={saving}
            fullWidth={isMobile}
          >
            {saving ? 'Saving...' : (isNew ? 'Save RAMS' : 'Update RAMS')}
          </Button>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Generated RAMS?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this generated RAMS? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setDeleteDialogOpen(false);
              if (onDelete) onDelete();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeneratedRAMSEditor;
